import store from '@/store'
import { SSharedAuth } from '@/services/shared'
import { SStudentAuth } from '@/services/student'

const AUTH_TIMEOUT_MS = 7000

const getPasswordChangeRouteName = userType => {
  return userType === 'student' ? 'student.force-password-change' : 'shared.force-password-change'
}

const shouldForcePasswordChange = user => {
  return Boolean(user?.must_change_password)
}

const withTimeout = (promise, timeout = AUTH_TIMEOUT_MS) => {
  return Promise.race([
    promise,
    new Promise((resolve, reject) => {
      setTimeout(() => reject(new Error('AUTH_TIMEOUT')), timeout)
    })
  ])
}

/* eslint-disable-next-line */
export default async (to, _from, next) => {
  const hasUser = store.getters['auth/user']
  const token = store.getters['auth/token']
  const userType = store.getters['auth/userType']

  if (token && hasUser?.id && shouldForcePasswordChange(hasUser)) {
    const forceRoute = getPasswordChangeRouteName(userType)

    if (to?.name !== forceRoute) {
      next({ name: forceRoute })
      return
    }
  }

  if (!token || hasUser?.id) {
    next()
    return
  }

  // Do not block rendering of the login page while probing stale tokens.
  if (to?.name === 'shared.login') {
    next()
    return
  }

  try {
    if (userType === 'student') {
      const { data: payload } = await withTimeout(SStudentAuth.getCurrentUser())
      const data = payload?.data || payload

      if (!data?.id) {
        throw new Error('Invalid student payload')
      }

      store.dispatch('auth/fetchUserSuccess', {
        id: data.id,
        email: data.email,
        must_change_password: data.must_change_password,
        role: 'student',
        role_name: 'Student',
        name: `${data.first_name} ${data.last_name}`,
        first_name: data.first_name,
        last_name: data.last_name,
        id_number: data.id_number,
        course: data.course,
        year_level: data.year_level,
        contact_number: data.contact_number,
        middle_name: data.middle_name,
        extension_name: data.extension_name
      })
    } else {
      const { data: payload } = await withTimeout(SSharedAuth.getCurrentUser())
      const data = payload?.data || payload

      if (!data?.id) {
        throw new Error('Invalid shared payload')
      }

      store.dispatch('auth/fetchUserSuccess', {
        id: data.id,
        email: data.email,
        must_change_password: data.must_change_password,
        role_id: data.role?.id,
        role: data.role?.role_code,
        role_name: data.role?.role_name,
        username: data.username,
        name: data.name,
        first_name: data.first_name,
        last_name: data.last_name
      })
    }

    if (shouldForcePasswordChange(store.getters['auth/user'])) {
      const forceRoute = getPasswordChangeRouteName(store.getters['auth/userType'])

      if (to?.name !== forceRoute) {
        next({ name: forceRoute })
        return
      }
    }

    next()
  } catch {
    // Clear stale token to prevent repeated auth probes on every route.
    store.dispatch('auth/logout')
    next()
  }
}
